import "./style.scss";
import { FC, useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../../BtnDefault/BtnDefault";
import { AOPMaltaStudentData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../../converterPtToRem";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import {
  templatesMaltaEnAdmissionCoursesSettings,
  templatesMaltaEnStudentCoursesSettings,
  templatesMaltaEnStudentSecondCoursesSettings,
  templatesMaltaEnStudentThirdCoursesSettings,
} from "@/src/constants/AdmissionOffice/Malta/AOMaltaEnConsts";
import { Nationalities } from "@/src/constants/globalConsts";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import CustomSwitch from "@/src/components/CustomSwitch/CustomSwitch";

interface IAOPFormValues {
  customize?: boolean;
  whose_doc: string;
  coursePrefix: string;
  currentDate: string;
  fullName: string;
  dateOfBirth: string;
  nationality: string;
  academicYear: string;
  passportNumber: string;
  yearOfStudy: string;
  yearStudyMode: string;
  course: string;
  selectedCourse: string;
  mediumOfInstruction: string;
  duration: string;
  studentBody: string;
  studentBodySecond: string;
  secondPageDate: string;
  studentBodyThird: string;
  file: File | null;
}

interface AOPConfigProps {
  dataForGeneration: AOPMaltaStudentData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaStudentData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaStudentData>>;
  setCustomize: React.Dispatch<React.SetStateAction<boolean>>;
}

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ dataForGeneration, setDataForGeneration, setBodyForPrev, setCustomize }) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      customize: false,
      studentBody: "",
      studentBodySecond: "",
      studentBodyThird: "",
      currentDate: formatDateForInput(today),
    },
  });
  const dispatch = useDispatch();
  const [content, setContent] = useState<string>("");
  const [studentContent, setStudentContent] = useState<string>("");
  const [studentContentSecond, setStudentContentSecond] = useState<string>("");
  const [studentContentThird, setStudentContentThird] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  const [isPreviewReady, setIsPreviewReady] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);

  const customize = watch("customize");
  const course = watch("course");
  const studentBody = watch("studentBody");
  const studentBodySecond = watch("studentBodySecond");
  const studentBodyThird = watch("studentBodyThird");

  // AUTOCOMPLETE ↓
  const offeredCourseAuto = useAutocomplete(Object.keys(templatesMaltaEnAdmissionCoursesSettings));
  const studentBodyAuto = useAutocomplete(Object.keys(templatesMaltaEnStudentCoursesSettings));
  const studentBodySecondAuto = useAutocomplete(Object.keys(templatesMaltaEnStudentSecondCoursesSettings));
  const studentBodyThirdAuto = useAutocomplete(Object.keys(templatesMaltaEnStudentThirdCoursesSettings));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);

  useEffect(() => {
    setCustomize(Boolean(customize));
  }, [customize]);

  // EDITORS
  useEffect(() => {
    if (course) {
      const key = course as keyof typeof templatesMaltaEnAdmissionCoursesSettings;
      const courseSettings = templatesMaltaEnAdmissionCoursesSettings[key];
      if (courseSettings) {
        setContent(courseSettings.tinyMCEContent);
        setValue("selectedCourse", courseSettings.selected_course);
        setValue("mediumOfInstruction", courseSettings.medium_of_instruction);
        setValue("duration", courseSettings.duration);
        setTitle(courseSettings.title);
      }
    }
  }, [course, setValue]);

  useEffect(() => {
    if (studentBody) {
      const key = studentBody as keyof typeof templatesMaltaEnStudentCoursesSettings;
      const studentSettings = templatesMaltaEnStudentCoursesSettings[key];
      setStudentContent(studentSettings ? studentSettings.tinyMCEContent : "");
    }
  }, [studentBody]);

  useEffect(() => {
    if (studentBodySecond) {
      const key = studentBodySecond as keyof typeof templatesMaltaEnStudentSecondCoursesSettings;
      const studentSettings = templatesMaltaEnStudentSecondCoursesSettings[key];
      setStudentContentSecond(studentSettings ? studentSettings.tinyMCEContent : "");
    }
  }, [studentBodySecond]);

  useEffect(() => {
    if (studentBodyThird) {
      const key = studentBodyThird as keyof typeof templatesMaltaEnStudentThirdCoursesSettings;
      const studentSettings = templatesMaltaEnStudentThirdCoursesSettings[key];
      setStudentContentThird(studentSettings ? studentSettings.tinyMCEContent : "");
    }
  }, [studentBodyThird]);

  const generateNewDocument = async () => {
    dispatch(setIsPreloyder(true));
    try {
      let response = await admissionOfficeRequests.createStudentDoc(dataForGeneration);
      if (response) {
        setIsGenerated(true);
      }
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
    setIsPreviewReady(false);
  };

  const handlePreview = () => {
    const watchedFields = {
      title: title,
      whose_doc: watch("whose_doc"),
      coursePrefix: watch("coursePrefix"),
      currentDate: watch("currentDate"),
      fullName: watch("fullName"),
      dateOfBirth: watch("dateOfBirth"),
      nationality: watch("nationality"),
      academicYear: watch("academicYear"),
      passportNumber: watch("passportNumber"),
      yearOfStudy: watch("yearOfStudy"),
      yearStudyMode: watch("yearStudyMode"),
      selectedCourse: watch("selectedCourse"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      duration: watch("duration"),
      secondPageDate: watch("secondPageDate"),
      file: watch("file"),
      customize: watch("customize"),
    };
    setDataForGeneration((prev) => ({
      ...prev,
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      coursePrefix: watchedFields.coursePrefix || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      academicYear: watchedFields.academicYear || "",
      passportNumber: watchedFields.passportNumber || "",
      yearOfStudy: watchedFields.yearOfStudy || "",
      yearStudyMode: watchedFields.yearStudyMode || "",
      course: watchedFields.selectedCourse || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      duration: watchedFields.duration || "",
      bodyStudentLetter: splitHtmlByBlocks(studentContent),
      secondBodyStudentLetter: customize ? splitHtmlByBlocks(studentContentSecond) : null,
      secondPageDate: watchedFields.secondPageDate ? parseDate(watchedFields.secondPageDate) : "",
      thirdBodyStudentLetter: customize ? splitHtmlByBlocks(studentContentThird) : null,
      file: watchedFields.file,
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      coursePrefix: watchedFields.coursePrefix || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      fullName: watchedFields.fullName || "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      nationality: watchedFields.nationality || "",
      academicYear: watchedFields.academicYear || "",
      passportNumber: watchedFields.passportNumber || "",
      yearOfStudy: watchedFields.yearOfStudy || "",
      yearStudyMode: watchedFields.yearStudyMode || "",
      course: watchedFields.selectedCourse || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      duration: watchedFields.duration || "",
      bodyStudentLetter: splitHtmlByBlocksWithFontConvert(studentContent),
      secondBodyStudentLetter: customize ? splitHtmlByBlocksWithFontConvert(studentContentSecond) : null,
      secondPageDate: watchedFields.secondPageDate ? parseDate(watchedFields.secondPageDate) : "",
      thirdBodyStudentLetter: customize ? splitHtmlByBlocksWithFontConvert(studentContentThird) : null,
      file: watchedFields.file,
    }));

    const hasContent = content.trim().length > 0 || studentContent.trim().length > 0;
    if (hasContent) {
      setIsPreviewReady(true);
      setIsGenerated(false);
    }
  };

  const handleStudentEditorChange = (newstudentContent: string) => setStudentContent(newstudentContent);
  const handleStudentSecondEditorChange = (newstudentContent: string) => setStudentContentSecond(newstudentContent);
  const handleStudentThirdEditorChange = (newstudentContent: string) => setStudentContentThird(newstudentContent);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as any);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>
        <p className="admission-office-page-config-title">STUDENT LETTER</p>
        <form className="admission-office-page-config-form">
          <div className="form-group-fields gap">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseDocAuto.query}
                onChange={whoseDocAuto.onChange}
                onSelect={(v) => {
                  whoseDocAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseDocAuto.isOpen}
                options={whoseDocAuto.filtered}
                error={!!errors.studentBody}
                required
                inputProps={{
                  register: register("whose_doc", { required: true }),
                  close: whoseDocAuto.close,
                  setOpen: whoseDocAuto.onClick,
                }}
                identifier={"whose"}
              />
            </div>
            <div className="form-group-fields-input coursePrefix">
              <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
            </div>
            <div className="form-group-fields-invoice">
              <Controller
                name="customize"
                control={control}
                render={({ field }) => <CustomSwitch label="Customize" value={field.value} onChange={field.onChange} />}
              />
            </div>
            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="currentDate" id="currentDate" type="date" {...register("currentDate", { required: true })} />
              {errors.currentDate && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth", { required: true })} />
              {errors.dateOfBirth && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
              required
              inputProps={{
                register: register("nationality", { required: true }),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
              }}
              identifier={"nationality"}
            />
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input academic-year">
              <div className="form-group-fields-input-label">
                <label htmlFor="academic-year">Academic Year</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="academic-year" id="academic-year" type="text" {...register("academicYear")} />
            </div>
            <div className="form-group-fields-input passport-number">
              <div className="form-group-fields-input-label">
                <label htmlFor="passport-number">Passport Number</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="passport-number" id="passport-number" type="text" {...register("passportNumber")} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input year-of-study">
              <div className="form-group-fields-input-label">
                <label htmlFor="year-of-study">Year of Study</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="year-of-study" id="year-of-study" type="text" {...register("yearOfStudy")} />
            </div>
            <div className="form-group-fields-input year-study-mode">
              <div className="form-group-fields-input-label">
                <label htmlFor="year-study-mode">Year Study Mode</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="year-study-mode" id="year-study-mode" type="text" {...register("yearStudyMode")} />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input course">
              <AutocompleteInput
                label="Select the offered course"
                value={offeredCourseAuto.query}
                onChange={offeredCourseAuto.onChange}
                onSelect={(v) => {
                  offeredCourseAuto.onSelect(v);
                  setValue("course", v);
                }}
                isOpen={offeredCourseAuto.isOpen}
                options={offeredCourseAuto.filtered}
                error={!!errors.course}
                required
                inputProps={{
                  register: register("course", { required: true }),
                  close: offeredCourseAuto.close,
                  setOpen: offeredCourseAuto.onClick,
                  setValue,
                  clearErrors,
                  name: "course",
                }}
                identifier={"offered-course-auto"}
              />
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="selected-сourse">
                  Selected course<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse", { required: true })} />
              {errors.selectedCourse && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="medium-of-instruction">
                  Medium of Instruction<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="medium-of-instruction"
                id="medium-of-instruction"
                type="text"
                {...register("mediumOfInstruction", { required: true })}
              />
              {errors.mediumOfInstruction && <p>This field is required</p>}
            </div>
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="duration">
                  Duration<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="duration" id="duration" type="text" {...register("duration", { required: true })} />
              {errors.duration && <p>This field is required</p>}
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input studentBody">
              <AutocompleteInput
                label="Selecting details for an student letter"
                value={studentBodyAuto.query}
                onChange={studentBodyAuto.onChange}
                onSelect={(v) => {
                  studentBodyAuto.onSelect(v);
                  setValue("studentBody", v);
                }}
                isOpen={studentBodyAuto.isOpen}
                options={studentBodyAuto.filtered}
                error={!!errors.studentBody}
                required
                inputProps={{
                  register: register("studentBody", { required: true }),
                  close: studentBodyAuto.close,
                  setOpen: studentBodyAuto.onClick,
                }}
                identifier={"student"}
              />
            </div>
          </div>
          <Editor
            value={studentContent}
            init={{
              height: 300,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: `
                body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                p { margin: 0; }
              `,
            }}
            onEditorChange={handleStudentEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />
          {customize && (
            <>
              <div className="form-group-fields-input secondPageDate">
                <div className="form-group-fields-input-label">
                  <label htmlFor="secondPageDate">
                    Date for second part student letter<span>*</span>
                  </label>
                  <div className="form-group-fields-input-label-line"></div>
                </div>
                <input className="secondPageDate" id="secondPageDate" type="date" {...register("secondPageDate", { required: true })} />
                {errors.secondPageDate && <p>This field is required</p>}
                <div className="form-group-fields-input-svg">
                  <AODSvgExport.DateInputSvg />
                </div>
              </div>
              <div className="form-group-fields">
                <div className="form-group-fields-input studentBodySecond">
                  <AutocompleteInput
                    label="Selecting second page template"
                    value={studentBodySecondAuto.query}
                    onChange={studentBodySecondAuto.onChange}
                    onSelect={(v) => {
                      studentBodySecondAuto.onSelect(v);
                      setValue("studentBodySecond", v);
                    }}
                    isOpen={studentBodySecondAuto.isOpen}
                    options={studentBodySecondAuto.filtered}
                    error={!!errors.studentBody}
                    required
                    inputProps={{
                      register: register("studentBodySecond", { required: true }),
                      close: studentBodySecondAuto.close,
                      setOpen: studentBodySecondAuto.onClick,
                    }}
                    identifier={"student-second"}
                  />
                </div>
              </div>
              <Editor
                value={studentContentSecond}
                init={{
                  height: 300,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: `
                    body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                    p { margin: 0; }
                  `,
                }}
                onEditorChange={handleStudentSecondEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
              <div className="form-group-fields">
                <div className="form-group-fields-input studentBodyThird">
                  <AutocompleteInput
                    label="Selecting third page template"
                    value={studentBodyThirdAuto.query}
                    onChange={studentBodyThirdAuto.onChange}
                    onSelect={(v) => {
                      studentBodyThirdAuto.onSelect(v);
                      setValue("studentBodyThird", v);
                    }}
                    isOpen={studentBodyThirdAuto.isOpen}
                    options={studentBodyThirdAuto.filtered}
                    error={!!errors.studentBody}
                    required
                    inputProps={{
                      register: register("studentBodyThird", { required: true }),
                      close: studentBodyThirdAuto.close,
                      setOpen: studentBodyThirdAuto.onClick,
                    }}
                    identifier={"student-third"}
                  />
                </div>
              </div>
              <Editor
                value={studentContentThird}
                init={{
                  height: 300,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: `
                    body { font-family: Arial, sans-serif; padding: 1rem; margin: 0; }
                    p { margin: 0; }
                  `,
                }}
                onEditorChange={handleStudentThirdEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
            </>
          )}
          <div className="form-group-fields">
            <div className="form-group-fields-input file">
              <label htmlFor="file">Chose photo:</label>
              <input
                type="file"
                id="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] ?? null;
                  setValue("file", file, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            </div>
          </div>
          <div className="form-group-buttons">
            <div className="form-group-buttons-pack">
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
              <BtnDefault disabled={!isGenerated}>
                <p>Download PDF</p>
                <AODSvgExport.DownloadSvg />
              </BtnDefault>
            </div>
            <div className="form-group-buttons-refresh" onClick={() => window.location.reload()}>
              <BtnDefault onlyIcon={true}>
                <AODSvgExport.RefreshSvg />
              </BtnDefault>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
